import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ACTIVITY_EVENTS, IDLE_TIMEOUT, WARNING_DURATION } from '~/constants/common';
import { selectAccessToken } from '~/store/covenants/selectors';
import useGetUserRole from './useGetUserRole';

interface IIdleTimeoutProps {
  onIdle?: () => void;
  onTimeout?: () => void;
}

export const useIdleTimeout = ({ onIdle, onTimeout }: IIdleTimeoutProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(WARNING_DURATION / 1000);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningEndRef = useRef<number | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showWarningRef = useRef(false);
  const token = useSelector(selectAccessToken);
  const { isClientLogin, isManagerLogin, isAssetManager } = useGetUserRole();
  const isActiveSession = (isClientLogin || isManagerLogin) && !!token && !isAssetManager;

  const clearAllTimers = useCallback(() => {
    idleTimerRef.current && clearTimeout(idleTimerRef.current);
    logoutTimerRef.current && clearTimeout(logoutTimerRef.current);
    countdownRef.current && clearInterval(countdownRef.current);
  }, []);

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    showWarningRef.current = false;
    setShowWarning(false);

    idleTimerRef.current = setTimeout(() => {
      showWarningRef.current = true;
      setShowWarning(true);
      warningEndRef.current = Date.now() + WARNING_DURATION;
      onIdle?.();

      countdownRef.current = setInterval(() => {
        if (!warningEndRef.current) return;
        const left = Math.max(Math.floor(warningEndRef.current - Date.now()) / 1000, 0);
        setSecondsLeft(left);
      }, 250);

      logoutTimerRef.current = setTimeout(() => {
        clearAllTimers();
        setShowWarning(false);
        showWarningRef.current = false;
        onTimeout?.();
      }, WARNING_DURATION);
    }, IDLE_TIMEOUT);
  }, [clearAllTimers, onIdle, onTimeout]);

  const stayActive = useCallback(() => {
    startIdleTimer();
  }, [startIdleTimer]);

  useEffect(() => {
    if (!isActiveSession) {
      clearAllTimers();
      setShowWarning(false);
      showWarningRef.current = false;
      return;
    }

    const handleActivity = () => {
      if (showWarningRef.current) return;
      startIdleTimer();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );

    startIdleTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      clearAllTimers();
    };
  }, [isActiveSession, startIdleTimer, clearAllTimers]);

  return { showWarning, secondsLeft, stayActive };
};
